import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { Switch } from '@/components/forms/Switch';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { VolumeMatchingTable } from '@/features/tenant/consolidatedExposure/VolumeMatchingTable';
import { EodSwapView } from '@/features/tenant/consolidatedExposure/EodSwapView';
import { formatNumber, formatDateTime } from '@/utils/format';
import { http } from '@/api/client/httpClient';
import { filterDataApi } from '@/api/services/filterData';
import { backdatedNopApi, backdatedLpApi, backdatedEodSwapApi } from '@/api/services/overnights';
import { BackdatedNopTable } from './BackdatedNopTable';
import { OvernightsPermissions as P } from './permissions';
import { NopType, type LPAccountSnapshotDto, type LpEodPositionDto, type NopSnapshotQuery } from '@/api/types/overnights';

const money = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));
const dt = (iso: string) => formatDateTime(iso);
const yn = (b: boolean) => (b ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />);

export function OvernightsPage() {
  const can = usePermission();
  const [view, setView] = useState<'hub' | 'nop' | 'lp'>('hub');

  return (
    <div className="overnights-page">
      <PageHeader title="Overnights" breadcrumb={['Home', 'Overnights']} />
      {view === 'hub' && (
        <div className="row g-3">
          {can(P.nopBackdated) && <HubCard title="NOP Backdated Report" icon="fe fe-layers" onClick={() => setView('nop')} />}
          {can(P.lpBackdated) && <HubCard title="LP Accounts Backdated Report" icon="fe fe-airplay" onClick={() => setView('lp')} />}
        </div>
      )}
      {view === 'nop' && <NopBackdated canByUid={can(P.byUniqueId)} canAutoMap={can(P.autoMap)} onBack={() => setView('hub')} />}
      {view === 'lp' && <LpBackdated onBack={() => setView('hub')} />}
    </div>
  );
}

function HubCard({ title, icon, onClick }: { title: string; icon: string; onClick: () => void }) {
  return (
    <div className="col-md-6 col-lg-4">
      <div className="card custom-card" role="button" onClick={onClick}>
        <div className="card-body d-flex align-items-center gap-3">
          <span className="avatar avatar-md bg-primary-transparent"><i className={`${icon} fs-18`} /></span>
          <div><div className="fw-semibold">{title}</div><div className="text-muted fs-12">View historical EOD snapshots</div></div>
        </div>
      </div>
    </div>
  );
}

function BackBar({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <div className="d-flex align-items-center mb-2 gap-2">
      <button type="button" className="btn btn-outline-primary btn-sm" onClick={onBack}><i className="fa fa-arrow-left me-1" />Back</button>
      <h6 className="mb-0 fw-semibold">{label}</h6>
    </div>
  );
}

// Snapshot-date filter for the backdated snapshot lists (legacy finkit-date-picker name="snapshotDate",
// max = yesterday, no default → empty means "all snapshots"). Uses the shared daterangepicker
// (single mode) so it looks like every other date picker in the system; the Clear button
// resets back to the empty "all snapshots" state.
function SnapshotDateFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yesterday = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
  return (
    <div className="d-flex align-items-end gap-2 mb-2">
      <div style={{ minWidth: 200 }}>
        <DateRangePicker
          label="Snapshot Date"
          mode="single"
          maxDate={yesterday}
          value={value || null}
          onChange={onChange}
          placeholder="All snapshots"
        />
      </div>
      {value && (
        <button type="button" className="btn btn-sm btn-outline-danger" title="Clear" onClick={() => onChange('')}><i className="fa fa-times" /></button>
      )}
    </div>
  );
}

// ---------------- NOP Backdated ----------------
interface NopFilters {
  login: string; instrument: string[]; datasourceId: string[]; mifidFilter: string[]; groupName: string[];
  portfolioId: number | null; byUniqueId: boolean; symbolMapping: boolean;
}
const emptyNopFilters = (): NopFilters => ({ login: '', instrument: [], datasourceId: [], mifidFilter: [], groupName: [], portfolioId: null, byUniqueId: false, symbolMapping: false });

function NopBackdated({ canByUid, canAutoMap, onBack }: { canByUid: boolean; canAutoMap: boolean; onBack: () => void }) {
  const [snapshotId, setSnapshotId] = useState<number | null>(null);
  const [snapshotDate, setSnapshotDate] = useState<string>('');
  const snapshots = useQuery({ queryKey: ['ov-nop-snaps', snapshotDate], queryFn: () => backdatedNopApi.getSnapshots(snapshotDate) });

  if (snapshotId == null) {
    const cols: DataGridColumn<{ id: number; snapshotAt: string; tradesStatus: boolean; dataSourceConversionsStatus: boolean; dataSourcePricesStatus: boolean; prerequisitesMet: boolean }>[] = [
      { key: 'snapshotAt', title: 'Snapshot', className: 'text-start', value: (r) => r.snapshotAt, cell: (r) => dt(r.snapshotAt) },
      { key: 'tradesStatus', title: 'Trades', className: 'text-center', value: (r) => String(r.tradesStatus), cell: (r) => yn(r.tradesStatus) },
      { key: 'dataSourceConversionsStatus', title: 'Conversions', className: 'text-center', value: (r) => String(r.dataSourceConversionsStatus), cell: (r) => yn(r.dataSourceConversionsStatus) },
      { key: 'dataSourcePricesStatus', title: 'Prices', className: 'text-center', value: (r) => String(r.dataSourcePricesStatus), cell: (r) => yn(r.dataSourcePricesStatus) },
      { key: 'open', title: '', className: 'text-end', value: () => '', cell: (r) => <button type="button" className="btn btn-primary btn-sm" disabled={!r.prerequisitesMet} onClick={() => setSnapshotId(r.id)}>Open</button> },
    ];
    return (
      <>
        <BackBar label="NOP Backdated Report" onBack={onBack} />
        <SnapshotDateFilter value={snapshotDate} onChange={setSnapshotDate} />
        <DataGrid columns={cols} data={snapshots.data?.items ?? []} rowKey={(r) => String(r.id)} pageSize={5} card emptyMessage={snapshots.isLoading ? 'Loading…' : 'No snapshots.'} />
      </>
    );
  }
  return <NopSnapshotDetail snapshotId={snapshotId} canByUid={canByUid} canAutoMap={canAutoMap} onBack={() => setSnapshotId(null)} />;
}

function NopSnapshotDetail({ snapshotId, canByUid, canAutoMap, onBack }: { snapshotId: number; canByUid: boolean; canAutoMap: boolean; onBack: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<NopFilters>(emptyNopFilters);
  const [applied, setApplied] = useState<NopFilters>(draft);
  const [active, setActive] = useState('clients');

  const instruments = useQuery({ queryKey: ['rd-filter', 'instruments'], queryFn: filterDataApi.getInstruments, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getConnectors, staleTime: 5 * 60_000 });
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const groups = useQuery({ queryKey: ['rd-filter', 'groups'], queryFn: filterDataApi.getGroups, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });

  const nopType = active === 'clients' ? NopType.ClientsNOP : active === 'firm' ? NopType.FirmNOP : active === 'lp' ? NopType.LPNOP : NopType.RTNOP;
  const baseQuery = (type: number): NopSnapshotQuery => ({
    snapshotId, nopType: type, isAutoMapping: applied.symbolMapping, byUniqueId: applied.byUniqueId, symbolMapping: applied.symbolMapping,
    login: applied.login || null,
    instrument: applied.instrument.length ? applied.instrument.join(',') : null,
    datasourceId: applied.byUniqueId || !applied.datasourceId.length ? null : applied.datasourceId.join(','),
    mifidFilter: applied.byUniqueId || !applied.mifidFilter.length ? null : applied.mifidFilter.join(','),
    groupName: applied.byUniqueId || !applied.groupName.length ? null : applied.groupName.join(','),
    portfolioId: applied.byUniqueId ? null : applied.portfolioId,
    skipCount: 0, maxResultCount: 1000,
  });

  const isNopTab = active === 'clients' || active === 'firm' || active === 'lp' || active === 'rt';
  const nopData = useQuery({ queryKey: ['ov-nop', snapshotId, nopType, applied], queryFn: () => backdatedNopApi.getNopSnapshot(baseQuery(nopType)), enabled: isNopTab });
  const nopTotals = useQuery({ queryKey: ['ov-nop-tot', snapshotId, nopType, applied], queryFn: () => backdatedNopApi.getTotals(baseQuery(nopType)), enabled: isNopTab });
  const volMatch = useQuery({ queryKey: ['ov-vm', snapshotId, applied], queryFn: () => backdatedNopApi.getVolumeMatching(baseQuery(NopType.ClientsNOP)), enabled: active === 'volume-matching' });
  const notifications = useQuery({ queryKey: ['ov-notif', snapshotId, applied], queryFn: () => backdatedNopApi.getNotifications(baseQuery(NopType.ClientsNOP)) });

  const recalc = async () => {
    try { await backdatedNopApi.recalculate(snapshotId); toast.success('EOD recalculation requested.'); } catch { toast.error('Failed to request recalculation.'); }
  };

  const uid = draft.byUniqueId;
  const tabs: TabItem[] = [
    { key: 'clients', title: 'Clients NOP' }, { key: 'firm', title: 'Firm NOP' }, { key: 'lp', title: 'LP NOP' }, { key: 'rt', title: 'R&T NOP' },
    { key: 'volume-matching', title: 'Volume Matching' }, { key: 'eod-swap', title: 'Expected EOD Swap' },
  ];

  const fields: React.ReactNode[] = [
    <div key="login"><label className="form-label">{uid ? 'Unique ID' : 'Login'}</label><input className="form-control" value={draft.login} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value }))} /></div>,
    <div key="instrument"><label className="form-label">Instrument</label><Select2Multi placeholder="All" value={draft.instrument} options={instruments.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, instrument: v }))} /></div>,
    ...(!uid ? [
      <div key="connector"><label className="form-label">Connector</label><Select2Multi placeholder="All" value={draft.datasourceId} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, datasourceId: v }))} /></div>,
      <div key="mifid"><label className="form-label">MiFID</label><Select2Multi placeholder="All" value={draft.mifidFilter} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, mifidFilter: v }))} /></div>,
      <div key="group"><label className="form-label">Group</label><Select2Multi placeholder="All" value={draft.groupName} options={groups.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, groupName: v }))} /></div>,
      <Select2 key="portfolio" label="Portfolio Profile" allowClear placeholder="All" value={draft.portfolioId == null ? '' : String(draft.portfolioId)} options={portfolios.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, portfolioId: v ? Number(v) : null }))} />,
    ] : []),
  ];

  return (
    <>
      <div className="d-flex align-items-center mb-2 gap-2">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={onBack}><i className="fa fa-arrow-left me-1" />Back</button>
        <h6 className="mb-0 fw-semibold me-auto">NOP Snapshot #{snapshotId}</h6>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={recalc}><i className="fa fa-sync me-1" />Recalculate EOD</button>
      </div>

      {notifications.data?.hasMissingSymbols && (
        <div className="alert alert-warning py-2">{notifications.data.missingSymbolsNotification || `Missing symbols: ${notifications.data.missingSymbols.join(', ')}`}</div>
      )}

      <FilterBar
        fields={fields}
        visibleCount={4}
        modes={
          <>
            {canAutoMap && <Switch label="Auto Map" checked={draft.symbolMapping} onChange={(c) => setDraft((f) => ({ ...f, symbolMapping: c }))} />}
            {canByUid && <Switch label="By Unique ID" checked={draft.byUniqueId} onChange={(c) => setDraft((f) => ({ ...f, byUniqueId: c, login: '', datasourceId: [], mifidFilter: [], groupName: [], portfolioId: null }))} />}
          </>
        }
        actions={
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setApplied({ ...draft })}><i className="fa fa-search me-1" />Fetch</button>
            <button type="button" className="btn btn-link btn-sm" onClick={() => { const e = emptyNopFilters(); setDraft(e); setApplied(e); }}>Clear All Filters</button>
          </>
        }
      />

      <div className="card custom-card mb-2">
        <div className="card-header p-0"><Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" /></div>
        <div className="card-body">
          {isNopTab && <BackdatedNopTable rows={nopData.data?.items ?? []} totals={nopTotals.data ?? null} excelName={`NOP ${active}`} card={false} />}
          {active === 'volume-matching' && <VolumeMatchingTable items={(volMatch.data?.items ?? []) as never} card={false} />}
          {active === 'eod-swap' && <BackdatedEodSwapTab query={baseQuery(NopType.ClientsNOP)} />}
        </div>
      </div>
    </>
  );
}

function BackdatedEodSwapTab({ query }: { query: NopSnapshotQuery }) {
  const toast = useToast();
  const q = useQuery({ queryKey: ['ov-eod', query], queryFn: () => backdatedEodSwapApi.getCalculated(query) });
  async function exportExcel() {
    try {
      const res = await http.get(backdatedEodSwapApi.exportUrl(query.snapshotId), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Expected EOD Swap - Snapshot ${query.snapshotId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Fetch the tab first, then try again.');
    }
  }
  return <EodSwapView snapshot={q.data} loading={q.isLoading} isError={q.isError} onRecalculate={() => q.refetch()} onExport={exportExcel} card={false} />;
}

// ---------------- LP Backdated ----------------
function LpBackdated({ onBack }: { onBack: () => void }) {
  const [snapshotId, setSnapshotId] = useState<number | null>(null);
  const [snapshotDate, setSnapshotDate] = useState<string>('');
  const snapshots = useQuery({ queryKey: ['ov-lp-snaps', snapshotDate], queryFn: () => backdatedLpApi.getSnapshots(snapshotDate) });

  if (snapshotId == null) {
    const cols: DataGridColumn<{ id: number; snapshotAt: string; lpAccountStatus: boolean }>[] = [
      { key: 'snapshotAt', title: 'Snapshot', className: 'text-start', value: (r) => r.snapshotAt, cell: (r) => dt(r.snapshotAt) },
      { key: 'lpAccountStatus', title: 'Status', className: 'text-center', value: (r) => String(r.lpAccountStatus), cell: (r) => yn(r.lpAccountStatus) },
      { key: 'open', title: '', className: 'text-end', value: () => '', cell: (r) => <button type="button" className="btn btn-primary btn-sm" disabled={!r.lpAccountStatus} onClick={() => setSnapshotId(r.id)}>Open</button> },
    ];
    return (
      <>
        <BackBar label="LP Accounts Backdated Report" onBack={onBack} />
        <SnapshotDateFilter value={snapshotDate} onChange={setSnapshotDate} />
        <DataGrid columns={cols} data={snapshots.data?.items ?? []} rowKey={(r) => String(r.id)} pageSize={5} card emptyMessage={snapshots.isLoading ? 'Loading…' : 'No snapshots.'} />
      </>
    );
  }
  return <LpSnapshotDetail snapshotId={snapshotId} onBack={() => setSnapshotId(null)} />;
}

function LpSnapshotDetail({ snapshotId, onBack }: { snapshotId: number; onBack: () => void }) {
  const [active, setActive] = useState('financials');
  const fin = useQuery({ queryKey: ['ov-lp-fin', snapshotId], queryFn: () => backdatedLpApi.getLpAccountSnapshot(snapshotId), enabled: active === 'financials' });
  const totals = useQuery({ queryKey: ['ov-lp-tot', snapshotId], queryFn: () => backdatedLpApi.getTotals(snapshotId), enabled: active === 'financials' });
  const positions = useQuery({ queryKey: ['ov-lp-pos', snapshotId], queryFn: () => backdatedLpApi.getLpEodPositions(snapshotId), enabled: active === 'positions' });

  const t = totals.data;
  const finCols: DataGridColumn<LPAccountSnapshotDto>[] = [
    { key: 'serverName', title: 'Server', className: 'text-start', value: (r) => r.serverName },
    { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
    { key: 'accountCurrency', title: 'Ccy', className: 'text-start', value: (r) => r.accountCurrency },
    { key: 'balanceRc', title: 'Balance', className: 'text-end', value: (r) => r.balanceRc, cell: (r) => money(r.balanceRc) },
    { key: 'creditRc', title: 'Credit', className: 'text-end', value: (r) => r.creditRc, cell: (r) => money(r.creditRc) },
    { key: 'floatingRc', title: 'Floating', className: 'text-end', value: (r) => r.floatingRc, cell: (r) => money(r.floatingRc) },
    { key: 'equityRc', title: 'Equity', className: 'text-end', value: (r) => r.equityRc, cell: (r) => money(r.equityRc) },
    { key: 'marginUsedRc', title: 'Margin Used', className: 'text-end', value: (r) => r.marginUsedRc, cell: (r) => money(r.marginUsedRc) },
    { key: 'freeMarginRc', title: 'Free Margin', className: 'text-end', value: (r) => r.freeMarginRc, cell: (r) => money(r.freeMarginRc) },
    { key: 'marginLevel', title: 'Margin Level %', className: 'text-end', value: (r) => r.marginLevel, cell: (r) => money(r.marginLevel) },
    { key: 'riskyVol', title: 'Risky Vol', className: 'text-end', value: (r) => r.riskyVol, cell: (r) => money(r.riskyVol) },
    { key: 'dataSourceName', title: 'Connector', className: 'text-start', value: (r) => r.dataSourceName },
  ];
  const totalsRow = t ? (
    <tr className="fw-semibold">
      <td className="text-start">Total</td><td /><td />
      <td className="text-end">{money(t.totalBalanceRc)}</td><td className="text-end">{money(t.totalCreditRc)}</td>
      <td className="text-end">{money(t.totalFloatingRc)}</td><td className="text-end">{money(t.totalEquityRc)}</td>
      <td className="text-end">{money(t.totalMarginUsedRc)}</td><td className="text-end">{money(t.totalFreeMarginRc)}</td>
      <td className="text-end">{money(t.averageMarginLevel)}</td><td className="text-end">{money(t.totalRiskyVol)}</td><td />
    </tr>
  ) : null;

  const posCols: DataGridColumn<LpEodPositionDto>[] = [
    { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
    { key: 'uniqueId', title: 'Unique Id', className: 'text-start', value: (r) => r.uniqueId },
    { key: 'instrument', title: 'Instrument', className: 'text-start', value: (r) => r.instrument },
    { key: 'type', title: 'Type', className: 'text-start', value: (r) => r.type },
    { key: 'volumeMT', title: 'Volume', className: 'text-end', value: (r) => r.volumeMT, cell: (r) => money(r.volumeMT, 2) },
    { key: 'openPrice', title: 'Open Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => money(r.openPrice, 5) },
    { key: 'openTime', title: 'Open Time', className: 'text-start', value: (r) => r.openTime, cell: (r) => dt(r.openTime) },
    { key: 'baseNotionalRc', title: 'Base Notional', className: 'text-end', value: (r) => r.baseNotionalRc, cell: (r) => money(r.baseNotionalRc) },
    { key: 'server', title: 'Server', className: 'text-start', value: (r) => r.server },
  ];

  const tabs: TabItem[] = [{ key: 'financials', title: 'EOD Financials' }, { key: 'positions', title: 'EOD Positions' }];
  return (
    <>
      <div className="d-flex align-items-center mb-2 gap-2">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={onBack}><i className="fa fa-arrow-left me-1" />Back</button>
        <h6 className="mb-0 fw-semibold">LP Accounts Snapshot #{snapshotId}</h6>
      </div>
      <div className="card custom-card mb-2">
        <div className="card-header p-0"><Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" /></div>
        <div className="card-body">
          {active === 'financials' && <DataGrid columns={finCols} data={fin.data?.items ?? []} rowKey={(r) => `${r.platformId}-${r.login}`} pageSize={5} card={false} includeExcel excelFileName="LP EOD Financials" footerTop={totalsRow} rowClassName={(r) => (r.isMissing ? 'text-muted' : '')} emptyMessage="No data." />}
          {active === 'positions' && <DataGrid columns={posCols} data={positions.data?.items ?? []} rowKey={(r) => String(r.order)} pageSize={5} card={false} includeExcel excelFileName="LP EOD Positions" emptyMessage="No data." />}
        </div>
      </div>
    </>
  );
}

